<template>
  <modal-vue @close="handleToggleModal" :modalActive="modals[modalName]">
    <div
      class="flex flex-col w-[full] h-[90vh] max-h-[60vh] overflow-y-auto bg-gray-50 rounded-lg p-2 mt-4"
    >
      <div class="cursor-pointer my-2 flex items-center justify-between">
        <span class="font-bold">Gestão dos vínculos da Política</span>

        <edit-pencil-icon v-if="!toggleEdit" @click="toggleEdit = !toggleEdit" color="#2F7F91" />
        <div v-if="toggleEdit" class="flex gap-2 text-xs text-gray-500 font-bold">
          <span @click="saveEditMode">
            <v-icon :icon="mdiCheckCircleOutline" color="#22bb33" class="cursor-pointer" />
            Salvar Rascunho
          </span>
          <span @click="closeEditMode">
            <v-icon :icon="mdiCloseCircleOutline" color="#bb2124" class="cursor-pointer" />
            Descartar Rascunho
          </span>
        </div>
      </div>

      <div class="flex w-full font-bold text-sm">
        <div class="w-[7%] text-start">#ID</div>
        <div class="w-[13%] text-start">Política</div>
        <div class="w-[19%] text-start">Início da Vigência</div>
        <div class="w-[20%] text-start">Final da Vigência</div>
        <div class="w-[20%] text-start">Data do Vínculo</div>
        <div class="w-[10%] text-start">Responsável</div>
        <div class="w-[5%]"></div>
      </div>

      <div
        v-for="(item, index) in draftItems"
        :key="index"
        class="grid grid-cols-12 gap-2 w-full items-start mt-3"
      >
        <div class="col-span-1">
          <GenericInput
            :disabled="true"
            v-model="item.policyId"
            name="linkId"
            density="comfortable"
            label="ID"
            variant="outlined"
            class="w-full"
            v-maska="'##########'"
          />
        </div>

        <div class="col-span-2">
          <GenericInput
            :disabled="true"
            v-model="item.policyName"
            name="politica"
            density="comfortable"
            label="Política"
            variant="outlined"
            class="w-full"
          />
        </div>

        <div class="col-span-2">
          <DatePicker
            v-model="item.startDate"
            locale="pt-BR"
            :masks="{ modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' }"
            is-required
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                label="Início"
                :model-value="inputValue"
                v-on="inputEvents"
                class="w-full"
                :disabled="!toggleEdit"
                color="primary20"
                variant="outlined"
                density="comfortable"
                :prepend-inner-icon="mdiCalendar"
              />
            </template>
          </DatePicker>
        </div>

        <div class="col-span-2">
          <DatePicker
            v-model="item.endDate"
            locale="pt-BR"
            :masks="{ modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' }"
            is-required
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                label="Final"
                :model-value="inputValue"
                v-on="inputEvents"
                class="w-full"
                :disabled="!toggleEdit"
                color="primary20"
                variant="outlined"
                density="comfortable"
                :prepend-inner-icon="mdiCalendar"
              />
            </template>
          </DatePicker>
        </div>

        <div class="col-span-2">
          <DatePicker
            v-model="item.linkedAt"
            locale="pt-BR"
            :masks="{ modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' }"
            is-required
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                label="Data"
                :model-value="inputValue"
                v-on="inputEvents"
                class="w-full"
                :disabled="true"
                color="primary20"
                variant="outlined"
                density="comfortable"
                :prepend-inner-icon="mdiCalendar"
              />
            </template>
          </DatePicker>
        </div>

        <div class="col-span-2">
          <GenericInput
            :disabled="true"
            v-model="item.linkedBy"
            name="responsável"
            density="comfortable"
            label="Responsável"
            variant="outlined"
            class="w-full"
          />
        </div>

        <div class="col-span-1 flex items-center justify-center">
          <button @click="removeItem(index)" :disabled="!toggleEdit">
            <v-icon :icon="mdiDeleteOutline" :color="colorHex" size="24" />
          </button>
        </div>
      </div>

      <div class="my-3 flex justify-between items-center gap-4">
        <div class="flex gap-2 items-center">
          <div class="w-[250px]">
            <ds-payment-policy-select v-model="selectedPolicy" />
          </div>

          <!-- <filled-button
            @click="addItem"
            :disabled="!toggleEdit || isLoading || !selectedPolicy"
            width="130px"
            height="40px"
            text="Adicionar linha"
            :loading="isLoading"
            density="comfortable"
          /> -->
        </div>

        <div>
          <filled-button
            @click="saveAllItems"
            :disabled="!canSaveFinalVersion"
            width="100px"
            height="40px"
            text="Salvar"
            :loading="isLoading"
          />
        </div>
      </div>
    </div>
  </modal-vue>
</template>

<script lang="ts" setup>
import { ref, PropType, computed, watch, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { EModals, useModalStore } from '@/pinia-store/useModalStore'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import DsPaymentPolicySelect from '@/components/designSystem/Select/DsPaymentPolicySelect.vue'

import {
  mdiCheckCircleOutline,
  mdiCloseCircleOutline,
  mdiDeleteOutline,
  mdiCalendar
} from '@mdi/js'
import EditPencilIcon from '@/assets/icons/edit-pencil.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { DatePicker } from 'v-calendar'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import PolicyService from '@/Network/Services/DsPolicyService'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'

type TableLinkPolicyItem = {
  policyId?: string
  policyName?: string
  startDate?: string
  endDate?: string
  linkedAt?: string
  linkedBy?: string
}

const props = defineProps({
  modelValue: {
    type: Array as PropType<TableLinkPolicyItem[]>,
    default: () => [] as TableLinkPolicyItem[]
  }
})
const emit = defineEmits(['update:modelValue', 'saveItems'])

const modalName = EModals.POLICIES_MODAL
const toast = useToast()
const { notifyError } = useErrorToast()

const modalStore = useModalStore()
const modals = storeToRefs(modalStore).modals

const DsPolicyService = new PolicyService()
const isLoading = ref(false)
const draftSaved = ref(false)
const toggleEdit = ref<boolean>(false)
const selectedPolicy = ref<any>(null)

const canSaveFinalVersion = computed(() => {
  return !toggleEdit.value && draftSaved.value && hasChangesToSave.value && !isLoading.value
})

const hasChangesToSave = computed(() => {
  const formattedCurrent = draftItems.value.map((item) => ({
    dsPolicyId: Number(item.policyId),
    policyName: item.policyName,
    startDate: item.startDate,
    endDate: item.endDate,
    linkedAt: item.linkedAt,
    linkedBy: item.linkedBy
  }))

  const formattedSaved = lastSavedPolicies.value.map((item) => ({
    dsPolicyId: Number(item.dsPolicyId),
    policyName: item.policyName,
    startDate: item.startDate,
    endDate: item.endDate,
    linkedAt: item.linkedAt,
    linkedBy: item.linkedBy
  }))

  return JSON.stringify(formattedCurrent) !== JSON.stringify(formattedSaved)
})

const colorHex = computed(() => (toggleEdit.value ? '#2F7F91' : '#D3D3D3'))

watch(
  () => modals.value[modalName],
  async (val) => {
    if (val) await fetchLinkedPolicies()
  }
)

watch(selectedPolicy, async (policy) => {
  if (!policy) return

  draftItems.value.push({
    policyId: policy.id,
    policyName: policy.name,
    startDate: '',
    endDate: '',
    linkedAt: new Date().toISOString().split('T')[0],
    linkedBy: policy.createdBy ?? ''
  })

  await nextTick()
  selectedPolicy.value = null
})
const draftItems = ref<Array<TableLinkPolicyItem>>([])

const lastSavedPolicies = ref<Array<any>>([])

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const initDraft = () => {
  draftItems.value = props.modelValue.map((item) => ({
    policyId: item.policyId ?? '',
    policyName: item.policyName ?? '',
    startDate: item.startDate ?? '',
    endDate: item.endDate ?? '',
    linkedAt: item.linkedAt ?? '',
    linkedBy: item.linkedBy ?? ''
  }))

  lastSavedPolicies.value = draftItems.value.map((item) => ({
    dsPolicyId: Number(item.policyId),
    policyName: item.policyName,
    startDate: item.startDate,
    endDate: item.endDate,
    linkedAt: item.linkedAt,
    linkedBy: item.linkedBy
  }))
}

const removeItem = (index: number) => {
  draftItems.value.splice(index, 1)
}

const closeEditMode = () => {
  toggleEdit.value = false
  draftSaved.value = false
  fetchLinkedPolicies()
}

const saveEditMode = () => {
  if (!hasChangesToSave.value) {
    toast.warning('Nenhuma alteração foi feita.')
    return
  }

  emit('update:modelValue', draftItems.value)
  emit('saveItems', draftItems.value)

  draftSaved.value = true
  toggleEdit.value = false
}

const handleToggleModal = () => {
  modalStore.toggleModal(modalName)
}

const fetchLinkedPolicies = async () => {
  try {
    isLoading.value = true
    const resourceId = modalStore.metadata?.resourceId as number
    let response

    switch (modalStore.metadata?.resourceType) {
      case 'company':
        response = await DsPolicyService.listCompanyDsPolicyLinks(resourceId)
        break
      case 'health_unit':
        response = await DsPolicyService.listHealthUnitDsPolicyLinks(resourceId)
        break
      case 'schedule':
        response = await DsPolicyService.listScheduleDsPolicyLinks(resourceId)
        break
      case 'position':
        response = await DsPolicyService.listPositionDsPolicyLinks(resourceId)
        break
    }

    if (!response || !response.data) {
      toast.error('Não foi possível carregar os dados da política.')
      return
    }

    draftItems.value = response.data.map((item: any) => ({
      policyId: item.dsPolicy?.id,
      policyName: item.dsPolicy?.name,
      startDate: item.startDate,
      endDate: item.endDate,
      linkedAt: item.createdAt,
      linkedBy: item.linkedBy?.name ?? ''
    }))

    lastSavedPolicies.value = draftItems.value.map((item) => ({
      dsPolicyId: Number(item.policyId),
      startDate: item.startDate,
      endDate: item.endDate
    }))
  } catch (e) {
    notifyError(e, 'Não foi possível carregar os vínculos da política.')
  } finally {
    isLoading.value = false
  }
}
const formatDate = (date: string | Date): string => {
  if (!date) return ''
  const d = new Date(date)
  d.setUTCHours(0, 0, 0, 0)
  return d.toISOString()
}

const saveAllItems = async () => {
  // Validação local FORA do try: preenchimento incompleto não pode passar pelo
  // funil de erro de API (`notifyError`), que exibiria a mensagem de falha ao
  // salvar e sugeriria um problema de rede/backend em vez do campo em branco.
  const hasMissingDates = draftItems.value.some((item) => !item.startDate || !item.endDate)
  if (hasMissingDates) {
    toast.error('Data de início ou fim ausente em uma das políticas.')
    return
  }

  try {
    const formattedItems = draftItems.value.map((item) => ({
      dsPolicyId: Number(item.policyId),
      startDate: formatDate(item.startDate ?? ''),
      endDate: formatDate(item.endDate ?? '')
    }))

    const hasEmptyFields = formattedItems.some((i) => !i.dsPolicyId || !i.startDate || !i.endDate)
    if (hasEmptyFields) {
      toast.error('Todos os campos são obrigatórios.')
      return
    }
    const invalidDateIndex = formattedItems.findIndex((item) => {
      const start = new Date(item.startDate)
      const end = new Date(item.endDate)
      return start > end
    })

    if (invalidDateIndex !== -1) {
      toast.error('A data de início não pode ser maior que a data final')
      return
    }

    const hasChanges =
      JSON.stringify(formattedItems) !==
      JSON.stringify(
        lastSavedPolicies.value.map((item) => ({
          dsPolicyId: Number(item.dsPolicyId),
          startDate: item.startDate,
          endDate: item.endDate
        }))
      )

    if (!hasChanges) {
      toast.warning('Nenhuma alteração foi feita.')
      return
    }

    isLoading.value = true
    const resourceId = modalStore.metadata?.resourceId as number

    switch (modalStore.metadata?.resourceType) {
      case 'company':
        await DsPolicyService.updateCompanyDsPolicyLinks(resourceId, {
          policies: formattedItems
        })
        break
      case 'health_unit':
        await DsPolicyService.updateHealthUnitDsPolicyLinks(resourceId, {
          policies: formattedItems
        })
        break
      case 'schedule':
        await DsPolicyService.updateScheduleDsPolicyLinks(resourceId, {
          policies: formattedItems
        })
        break
      case 'position':
        await DsPolicyService.updatePositionDsPolicyLinks(resourceId, {
          policies: formattedItems
        })
        break
    }

    toast.success('Vínculos atualizados com sucesso!')
    modalStore.toggleModal(modalName)
    toggleEdit.value = false
  } catch (e) {
    // Antes: extrator concorrente (`e?.response?.data?.message`) que ficava MUDO
    // quando o erro não tinha esse shape. `notifyError` sempre notifica.
    console.error('Erro ao salvar os vínculos da política:', e)
    notifyError(e, 'Não foi possível salvar os vínculos da política.')
  } finally {
    isLoading.value = false
  }
}
</script>
